import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, timer } from 'rxjs';
import { NotificationService } from '../../shared/notification-service/notification.service';
import {
  CustomerSubscriptionsService,
  Subscription,
  ProductsService,
} from 'ldt-billing-service-api';
import { catchError, tap, finalize, startWith, share } from 'rxjs/operators';
import { AuthService } from '../../auth/service/auth.service';
import { Log } from 'src/app/logger.service';
import { customerProducts } from 'src/environments/environment.prod';

export interface ProductPrice {
  priceId: string;
  price: number;
  qty: number;
}

export interface Product {
  id: string;
  prices: ProductPrice[];
}

@Injectable({
  providedIn: 'root',
})
export class BillingService {
  private subscriptionsSource = new BehaviorSubject<Subscription[] | null>(null);
  private refreshingSubscriptions = new BehaviorSubject<boolean>(false);
  private productsSource = new BehaviorSubject<Product[] | null>(null);
  private refreshingProducts = new BehaviorSubject<boolean>(false);
  private handledStripeReturn = false;

  subscriptions$: Observable<Subscription[] | null> = this.subscriptionsSource.asObservable().pipe(
    startWith(null),
    tap(() => {
      if (!this.subscriptionsSource.value && !this.refreshingSubscriptions.value) {
        this.getSubscriptions();
      }
    })
  );

  refreshingSubscriptions$ = this.refreshingSubscriptions.asObservable();

  products$: Observable<Product[] | null> = this.productsSource.asObservable().pipe(
    startWith(null),
    tap(() => {
      if (!this.productsSource.value && !this.refreshingProducts.value) {
        this.getProducts();
      }
    })
  );

  refreshingProducts$ = this.refreshingProducts.asObservable();

  constructor(
    private notify: NotificationService,
    private customerSubscriptionsService: CustomerSubscriptionsService,
    private authService: AuthService,
    private productsService: ProductsService
  ) {
    // Reset the org-specific observables when org changes
    this.authService.$getSelectedOrg.subscribe(() => {
      this.subscriptionsSource.next(null);
      this.refreshingSubscriptions.next(false);
    });
  }

  public refreshAllData(): void {
    this.getSubscriptions();
    this.getProducts();
  }

  public refreshSubscriptions(): void {
    this.getSubscriptions();
  }

  /**
   * Handle a return from Stripe checkout (`?stripeResults=success|...`). Stripe
   * can redirect back to any route (e.g. `/chat`), so this is invoked globally
   * rather than only on the billing page. On success we refresh subscriptions
   * (plan) and, after giving Stripe's webhook a moment to update the org's
   * entitlements, refresh the session so the new quota/limits flow through the
   * app (sidebar gauge, composer, etc.). Safe to call with any query params —
   * it no-ops when `stripeResults` is absent and is guarded against running
   * twice for the same return.
   */
  public handleStripeReturn(params: Record<string, any>): void {
    if (!params || !('stripeResults' in params) || this.handledStripeReturn) {
      return;
    }
    this.handledStripeReturn = true;

    this.refreshSubscriptions();

    if (params['stripeResults'] !== 'success') {
      this.notify.error(
        'There was a problem processing your subscription. Please try again later.'
      );
      return;
    }

    this.notify.success('Thanks for your subscription!');
    this.refreshAfterCheckout();
  }

  /**
   * Refresh entitlements after a successful checkout. Stripe finalizes the
   * purchase via an async webhook, so the org's new subscription + quota limits
   * can lag the redirect. We poll subscriptions on a backoff and, once they
   * change (or we've waited long enough), refresh the session so the new limits
   * propagate to the app (sidebar gauge, composer, etc.).
   */
  private refreshAfterCheckout(): void {
    const baselineSignature = this.subscriptionSignature();
    const delaysMs = [2000, 3000, 5000, 8000];
    let attempt = 0;

    const poll = () => {
      timer(delaysMs[attempt]).subscribe(() => {
        attempt++;
        // Decide on the RESULT of this fetch, not the cache. getSubscriptions()
        // updates the cache asynchronously, so reading the signature right after
        // it would compare stale (pre-fetch) data and never detect the change;
        // fetchSubscriptions() hands us the fresh list to compare directly.
        this.fetchSubscriptions().subscribe((subs) => {
          const changed = this.signatureOf(subs) !== baselineSignature;
          if (changed || attempt >= delaysMs.length) {
            // Entitlements look ready (or we've waited the full schedule): pull a
            // fresh session so the updated quota/limits flow through.
            this.authService.refreshSession().subscribe();
          } else {
            poll();
          }
        });
      });
    };
    poll();
  }

  private subscriptionSignature(): string {
    return this.signatureOf(this.subscriptionsSource.value ?? []);
  }

  private signatureOf(subs: Subscription[]): string {
    return subs
      .map((sub) => `${sub.id}:${sub.product?.price?.id ?? ''}`)
      .sort()
      .join('|');
  }

  public refreshProducts(): void {
    this.getProducts();
  }

  private getSubscriptions(): void {
    if (this.refreshingSubscriptions.value) {
      return;
    }
    this.fetchSubscriptions().subscribe();
  }

  // Fetch the org's subscriptions, push them into the shared cache, and hand the
  // fresh list back so callers (e.g. the post-checkout poll) can react to the
  // result directly rather than to the asynchronously-updated cache.
  private fetchSubscriptions(): Observable<Subscription[]> {
    this.refreshingSubscriptions.next(true);

    return this.customerSubscriptionsService
      .getAllOrgSubscriptions(this.authService.getSelectedOrgIdValue)
      .pipe(
        tap((response: Subscription[]) => {
          Log.d('BillingService: getSubscriptions: response:', response);
          this.subscriptionsSource.next(response || []);
        }),
        catchError((error) => {
          this.notify.error('Error getting subscriptions.');
          Log.e('BillingService: getSubscriptions: error:', error);
          this.subscriptionsSource.next([]);
          return of([] as Subscription[]);
        }),
        finalize(() => this.refreshingSubscriptions.next(false)),
        share()
      );
  }

  private getProducts(): void {
    if (this.refreshingProducts.value) {
      return;
    }
    this.refreshingProducts.next(true);

    this.productsSource.next(customerProducts);
  }
}
